import React, { useRef, useState, useMemo, useCallback } from 'react'

import { FormikProps, FormikValues } from 'formik'

import { IOptionProps, useToast } from '@pnld/components-web'
import { useQueryClient } from '@tanstack/react-query'

import {
  useQueryGetFiles,
  useQueryGetProcessingUsers,
} from '@/api/prediction-processing/queries'
import {
  TGetFilesParams,
  TGetFilesResponse,
} from '@/api/prediction-processing/types'
import { useQueryGetCheckPredictions } from '@/api/predictions/queries'
import useDebounce from '@/hooks/useDebounce'

import { formatDate } from './shared'

const usePaginationController = (
  filesData: TGetFilesResponse | undefined,
  setFilters: React.Dispatch<React.SetStateAction<TGetFilesParams>>
) => {
  const itemsPerPageOptions: Array<IOptionProps> = useMemo(
    () => [
      { label: '10', value: 10 },
      { label: '20', value: 20 },
      { label: '30', value: 30 },
      { label: '40', value: 40 },
      { label: '50', value: 50 },
    ],
    []
  )

  const pageJumpOptions: Array<IOptionProps> = useMemo(() => {
    const totalPages = filesData?.pagination.totalPages || 1
    return Array.from({ length: totalPages }, (_, i) => ({
      label: `${i + 1}`,
      value: i,
    }))
  }, [filesData?.pagination.totalPages])

  const handleSelectNextPage = () => {
    setFilters(prev => ({
      ...prev,
      page: (prev.page || 1) + 1,
    }))
  }

  const handleSelectPreviousPage = () => {
    setFilters(prev => ({
      ...prev,
      page: Math.max((prev.page || 1) - 1, 1),
    }))
  }

  const handleChangeItemsPerPage = (itemsPerPage: number) => {
    setFilters(prev => ({
      ...prev,
      pageSize: itemsPerPage,
      page: 1,
    }))
  }

  const handleJumpToPage = (newPageIndex: number) => {
    setFilters(prev => ({
      ...prev,
      page: newPageIndex + 1,
    }))
  }

  return {
    paginationProps: {
      currentPageIndex: (filesData?.pagination.currentPage || 1) - 1,
      itemsPerPage: filesData?.pagination.pageSize || 10,
      numberOfPages: filesData?.pagination.totalPages || 1,
      numberOfItems: filesData?.pagination.count || 0,
      choicesOfItemsPerPage: itemsPerPageOptions,
      choicesOfPageJump: pageJumpOptions,
      onSelectPreviousPage: handleSelectPreviousPage,
      onSelectNextPage: handleSelectNextPage,
      onJumpToPage: handleJumpToPage,
      onChangeItemsPerPage: handleChangeItemsPerPage,
      disableGoToPreviousPage: (filesData?.pagination.currentPage || 1) <= 1,
      disableGoToNextPage:
        (filesData?.pagination.currentPage || 1) >=
        (filesData?.pagination.totalPages || 1),
    },
  }
}

const CHECK_PREDICTIONS_QUERY_KEY = ['predictions', 'check']

const useSendingsFilesController = ({ onClose }: { onClose: () => void }) => {
  const toast = useToast()
  const queryClient = useQueryClient()
  const formikRef = useRef<FormikProps<FormikValues>>(null)
  const [filters, setFilters] = useState<TGetFilesParams>({
    page: 1,
    pageSize: 10,
  })

  const { data: filesData, isLoading, isFetching } = useQueryGetFiles(filters)

  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: useQueryGetFiles.queryKey })
    queryClient.invalidateQueries({ queryKey: CHECK_PREDICTIONS_QUERY_KEY })
  }, [queryClient])

  const { data: processingUsers } = useQueryGetProcessingUsers()
  const userOptions: Array<IOptionProps> = useMemo(() => {
    if (!processingUsers) return []
    return processingUsers.map(user => ({
      label: user.noUserName || user.dsEmail,
      value: user.coUser,
    }))
  }, [processingUsers])

  const tableData = useMemo(() => {
    if (!filesData?.results) return []
    return filesData.results.map(file => ({
      id: file.coProcessing,
      filename: file.dsOriginalFilename,
      username: file.coUser?.noUserName || file.coUser?.dsEmail || '—',
      sendDate: formatDate(file.dtCreatedAt),
      censusYear: file.nuCensusYear,
      predictionYear: file.nuPredictionTargetYear,
      statusCode: file.dsStatus,
      statusDisplay: file.dsStatusDisplay,
      resultMessage: file.dsResultMessage,
    }))
  }, [filesData?.results])

  const updateFilters = useCallback((newFilters: Partial<TGetFilesParams>) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters,
      page: 1,
    }))
  }, [])

  const debouncedUpdateFilters = useDebounce<Partial<TGetFilesParams>>(
    updateFilters,
    500
  )

  const handleSearchChange = (search: string) => {
    debouncedUpdateFilters({ dsOriginalFilename: search || undefined })
  }

  const handleStartDateChange = (date: string) => {
    debouncedUpdateFilters({ startDate: date || undefined })
  }

  const handleFinalDateChange = (date: string) => {
    debouncedUpdateFilters({ finalDate: date || undefined })
  }

  const handleUserChange = (coUser: number | undefined) => {
    updateFilters({ coUser })
  }

  const handleSubmit = (_values: FormikValues) => {
    // Invalida ambas as queries para refetch imediato após o upload:
    // - 'files': atualiza a tabela com o novo registro de processamento
    // - 'predictions/check': atualiza processingPrediction para ativar o polling
    queryClient.invalidateQueries({ queryKey: useQueryGetFiles.queryKey })
    queryClient.invalidateQueries({ queryKey: CHECK_PREDICTIONS_QUERY_KEY })
    toast({
      title: 'Dados enviados com sucesso',
      description: 'O processamento foi iniciado e pode levar alguns minutos.',
      status: 'success',
      duration: 5000,
      isClosable: true,
    })
    onClose()
  }

  return {
    fileProps: {
      formikRef,
      handleSearchChange,
      handleStartDateChange,
      handleFinalDateChange,
      handleUserChange,
      userOptions,
      handleSubmit,
      handleRefresh,
      tableData,
      isLoading,
      isFetching,
    },
    filesData,
    setFilters,
  }
}

export const useSendingsController = ({ onClose }: { onClose: () => void }) => {
  // Verificação de processamento ativo ANTES de useSendingsFilesController para
  // que processingPrediction possa controlar o refetchInterval da query de arquivos.
  const { data: checkPredictionsData } = useQueryGetCheckPredictions({
    enabled: true,
  })
  const processingPrediction = !!checkPredictionsData?.processingPrediction

  const { fileProps, filesData, setFilters } = useSendingsFilesController({
    onClose,
  })
  const { paginationProps } = usePaginationController(filesData, setFilters)

  return {
    ...fileProps,
    ...paginationProps,
    processingPrediction,
  }
}
